// Class
import _ from "lodash";
import { DateUtils } from "../utils/DateUtils";
import moment from "moment";
import { JournalEntry } from "./JournalEntry";
import { Routes } from "../utils/Routes";

export class Employe {
    id?: number;
    nom: string = "";
    prenom: string = "";
    alt_noms: string = "";
    civilite: Employe.ECivilite = Employe.ECivilite.Madame;
    date_entree_entreprise: ?string;
    is_test: boolean = false;
    has_acquisition_conges_initial_date: boolean = false;
    heures_avenant: number = 0;
    heures_contrat: number = 0;
    disponibilites?: number;
    acquisition_conges?: Employe.AcquisitionConges;

    constructor(data: any) {
        let self = this;
        _.assign(this, _.omit(data, "acquisition_conges"));
        if (data.acquisition_conges) {
            self.acquisition_conges = new Employe.AcquisitionConges(
                data.acquisition_conges
            );
        }
    }

    get civilite_abbr(): string {
        return Employe.Civilite[this.civilite].abbr;
    }

    get fullName(): string {
        return this.nom + " " + this.prenom;
    }

    get date_entree_entreprise_str(): string {
        return DateUtils.sqlToDateStr(this.date_entree_entreprise);
    }

    set date_entree_entreprise_str(date: string) {
        var newDate = DateUtils.dateStrToSql(date);

        if (!_.isEmpty(newDate)) {
            this.date_entree_entreprise = newDate;
        }
    }

    serialize(): Employe.IEmploye {
        return {
            id: this.id,
            nom: this.nom,
            prenom: this.prenom || "",
            alt_noms: this.alt_noms || "",
            civilite: this.civilite,
            date_entree_entreprise: this.date_entree_entreprise,
            is_test: this.is_test,
        };
    }

    get link(): string {
        return Routes.employe_page + this.id;
    }
}

export namespace Employe {
    export enum ECivilite {
        Madame = "Madame",
        Monsieur = "Monsieur",
    }

    export interface ICivilite {
        id: ECivilite;
        name: string;
        abbr: string;
    }

    export const Civilite: { [index: number]: ICivilite } = {
        [ECivilite.Madame]: {
            id: ECivilite.Madame,
            name: "Madame",
            abbr: "M<sup>me</sup>",
        },
        [ECivilite.Monsieur]: {
            id: ECivilite.Monsieur,
            name: "Monsieur",
            abbr: "M.",
        },
    };

    export interface IEmploye {
        id?: number;
        nom: string;
        prenom: string;
        alt_noms: string;
        civilite: ECivilite;
        date_entree_entreprise?: string;
        is_test: boolean;
    }

    export class Planning {
        id?: number;
        employe_id?: number;
        planning_id: number = 1;
        repetition: number = 1;
        start_date?: string;
        planning_working_hours: Array<PlanningWorkingHour> = [];

        constructor(data: any) {
            let self = this;
            _.assign(self, _.omit(data, ["planning_working_hours"]));
            if (data.planning_working_hours) {
                _.forEach(data.planning_working_hours, function (working_hour) {
                    self.planning_working_hours.push(
                        new PlanningWorkingHour(working_hour)
                    );
                });
            }
        }

        get weekly_hours(): number {
            let total = 0;
            _.forEach(this.planning_working_hours, function (working_hour) {
                total += moment(working_hour.heure_fin, "HH[h]mm").diff(
                    moment(working_hour.heure_debut, "HH[h]mm"),
                    "hours",
                    true
                );
            });
            if (total % 1 != 0) {
                // décimal
                return _.floor(total, 1).toFixed(2);
            } else {
                // entier
                return total;
            }
        }

        get start_date_str(): string {
            return DateUtils.sqlToDateStr(this.start_date);
        }

        set start_date_str(date: string) {
            var newDate = DateUtils.dateStrToSql(date);
            if (!_.isEmpty(newDate)) {
                this.start_date = newDate;
            }
        }
    }

    export class PlanningWorkingHour {
        id?: number;
        jour?: EWorkingHourDay;
        heure_debut?: string;
        heure_fin?: string;

        constructor(data: any) {
            _.assign(this, data);
        }

        toEvent(weekStart): any {
            let day = moment(weekStart)
                .day(this.jour != 0 ? this.jour : 7)
                .format("YYYY-MM-DD");
            let start_date = day + " " + this.heure_debut.replace("h", ":");
            let end_date = day + " " + this.heure_fin.replace("h", ":");
            return {
                start: start_date,
                end: end_date,
                background: true,
                class: "working-hours",
            };
        }
    }

    export enum EWorkingHourDay {
        Sunday = 0,
        Monday = 1,
        Tuesday = 2,
        Wednesday = 3,
        Thursday = 4,
        Friday = 5,
        Saturday = 6,
    }

    export class Absence {
        id?: number;
        private _employe?: Employe;
        employe_id?: number;
        date_debut?: string;
        date_fin?: string;
        duree?: number;
        motif: string = "";
        is_conge: boolean = false;
        is_notified: boolean = false;
        to_notify: boolean = false;
        private _journal_entry?: JournalEntry;
        journal_entry_id?: number;

        constructor(data: any) {
            _.assign(this, _.omit(data, ["employe", "journal_entry"]));
            if (data.employe) {
                this._employe = new Employe(data.employe);
            }
            if (data.journal_entry) {
                this._journal_entry = new JournalEntry(data.journal_entry);
            }
        }

        //
        get date_debut_str(): string {
            return DateUtils.sqlToDateStr(this.date_debut);
        }

        set date_debut_str(date: string) {
            var newDate = DateUtils.dateStrToSql(date);

            if (!_.isEmpty(newDate)) {
                this.date_debut = newDate;
            }
        }

        //
        get date_fin_str(): string {
            return DateUtils.sqlToDateStr(this.date_fin);
        }

        set date_fin_str(date: string) {
            var newDate = DateUtils.dateStrToSql(date);

            if (!_.isEmpty(newDate)) {
                this.date_fin = newDate;
            }
        }

        //
        get semaine_str(): string {
            let semaineDebut = moment(this.date_debut).week().toString();
            let semaineFin = moment(this.date_fin).week().toString();
            return (
                semaineDebut +
                (semaineFin != semaineDebut ? " - " + semaineFin : "")
            );
        }

        //
        get employe(): Employe | undefined {
            return this._employe;
        }

        set employe(value: Employe | undefined) {
            if (value) {
                this._employe = value;
                this.employe_id = value.id;
            }
        }

        //
        get journal_entry(): JournalEntry | undefined {
            return this._journal_entry;
        }
    }

    export class Contrat {
        id?: number;
        private _employe?: Employe;
        employe_id?: number;
        start_date: string = moment().format("YYYY-MM-DD");
        end_date: string | null = null;
        hours: number = 0;
        avenants: Array<Avenant> = [];

        constructor(data: any) {
            let self = this;

            _.assign(this, _.omit(data, ["employe", "avenants"]));
            if (data.employe) {
                this._employe = new Employe(data.employe);
            }
            if (data.avenants) {
                _.forEach(data.avenants, function (avenant) {
                    self.avenants.push(new Avenant(avenant));
                });
            }
        }

        //
        get start_date_str(): string {
            return DateUtils.sqlToDateStr(this.start_date);
        }

        set start_date_str(date: string) {
            var newDate = DateUtils.dateStrToSql(date);

            if (!_.isEmpty(newDate)) {
                this.start_date = newDate;
            }
        }

        //
        get end_date_str(): string {
            return DateUtils.sqlToDateStr(this.end_date);
        }

        set end_date_str(date: string) {
            var newDate = DateUtils.dateStrToSql(date);

            if (!_.isEmpty(newDate)) {
                this.end_date = newDate;
            } else {
                this.end_date = null;
            }
        }

        //
        get employe(): Employe | undefined {
            return this._employe;
        }

        set employe(value: Employe | undefined) {
            if (value) {
                this._employe = value;
                this.employe_id = value.id;
            }
        }
    }

    export class Avenant {
        id?: number;
        start_date: string = moment().format("YYYY-MM-DD");
        end_date: string | null = null;
        hours: number = 0;

        constructor(data: any) {
            _.assign(this, data);
        }

        //
        get start_date_str(): string {
            return DateUtils.sqlToDateStr(this.start_date);
        }

        set start_date_str(date: string) {
            var newDate = DateUtils.dateStrToSql(date);

            if (!_.isEmpty(newDate)) {
                this.start_date = newDate;
            }
        }

        //
        get end_date_str(): string {
            return DateUtils.sqlToDateStr(this.end_date);
        }

        set end_date_str(date: string) {
            var newDate = DateUtils.dateStrToSql(date);

            if (!_.isEmpty(newDate)) {
                this.end_date = newDate;
            } else {
                this.end_date = null;
            }
        }
    }

    export interface IVehiculeType {
        id: EVehiculeType;
        name: string;
    }
    export enum EVehiculeType {
        Societe = 0,
        Personnel = 1,
    }

    export const VehiculeType: { [index: number]: IVehiculeType } = {
        [EVehiculeType.Societe]: {
            id: EVehiculeType.Societe,
            name: "Véhicule Société",
        },
        [EVehiculeType.Personnel]: {
            id: EVehiculeType.Personnel,
            name: "Véhicule Personnel",
        },
    };

    export class Vehicule {
        id?: number;
        type_vehicule: EVehiculeType = EVehiculeType.Societe;
        immatriculation: string = "";

        constructor(data: any) {
            _.assign(this, data);
        }
    }

    export class NoteDeFrais {
        id?: number;
        month: DateUtils.Month = { year: 0, month: 0 };
        total_km: number = 0;
        taux_km: number = 0;
        montant_km: number = 0;
        stationnement: number = 0;
        carburant: number = 0;
        divers: number = 0;
        files: Array<any> = [];
        filesIds: Array<number> = [];
        pdfFile: any = null;

        constructor(data: any) {
            _.assign(this, _.omit(data, ["month"]));
            if (data.month) {
                let date = moment(data.month);
                if (date.isValid()) {
                    this.month = {
                        year: date.year(),
                        month: date.month() + 1,
                    };
                }
            }
        }

        get total(): number {
            return (
                this.montant_km +
                this.stationnement +
                this.carburant +
                this.divers
            );
        }
    }

    export class AcquisitionConges {
        year: int = moment().year();
        initial_year: boolean = false;
        compteur_periode: number = 0;
        conges_pris_periode: number = 0;
        solde_conges_periode: number = 0;
        jours_conges_acquis: number = 0;
        jours_conges_pris: number = 0;
        solde_conges: number = 0;
        start_date: string | null = null;
        end_date: string | null = null;

        constructor(data: any) {
            _.assign(this, data);
        }
    }
}
